<script setup lang="ts">
import { computed } from 'vue'

import {
  isCaptain,
  LineupModel,
  Lineups,
  LineupsTitle,
  useLineupModel,
  useSeasonSquad,
} from '@collector/desktop-feature-lineups'
import { Modal, runLoader } from '@collector/shared-ui'
import { Participant, SubParticipant } from '@collector/sportsapi-types'
import { useEventId, useSeason } from '@desktop/views/Relation/composables'
import {
  UpdateLineupsFn,
  useLineupConfig,
  useProbableLineupDeferredClear,
} from '@desktop/views/Relation/Sports/composables'

import ClearProbableLineupOverlay from './Overlays/ClearProbableLineupOverlay.vue'

interface Props {
  participant: Participant
  confirmUpdateLineup: (
    lineup: LineupModel,
    initialLineup: LineupModel,
    updateLineups: UpdateLineupsFn,
    captain?: SubParticipant | null,
    isPendingDeferredClear?: boolean,
  ) => Promise<void>
}
const props = defineProps<Props>()

type Emits = { close: [] }
const emit = defineEmits<Emits>()

const { season } = useSeason()
const eventId = useEventId()
const { isParticipantPendingDeferredClear } = useProbableLineupDeferredClear()

const {
  config,
  createLineupModel,
  updateLineups,
  toSortedLineupModel,
  hasPositionChanged,
  sortSquad,
  transformSubParticipants,
} = useLineupConfig()

async function loadLineupSubParticipants(
  players: SubParticipant[],
): Promise<SubParticipant[]> {
  if (isParticipantPendingDeferredClear(props.participant.id)) return []

  if (transformSubParticipants)
    return transformSubParticipants(eventId.value, players)

  return players
}

const { lineup, initialLineup, updateLineupModel, fetchLineup } =
  useLineupModel(
    eventId,
    () => props.participant.id,
    config.value.triggerSortOnLineupModelUpdate ?? true,
    createLineupModel,
    toSortedLineupModel,
    loadLineupSubParticipants,
  )

const { seasonSquadSubParticipants, fetchSeasonSquad } = useSeasonSquad(
  season,
  () => props.participant,
  { compareFn: sortSquad },
)

const hasChanges = computed(() => {
  const keys = Object.keys(lineup.value) as PropertyKey[]

  return keys.some((key) =>
    hasPositionChanged(key, lineup.value, initialLineup.value),
  )
})

const captain = computed(
  () => Object.values(lineup.value).flat().find(isCaptain) ?? null,
)

const isPendingDeferredClear = computed(() =>
  isParticipantPendingDeferredClear(props.participant.id),
)

const hasLineupContent = computed(() =>
  Object.values(lineup.value).some((players) => players.length > 0),
)

const canSave = computed(() => {
  if (isPendingDeferredClear.value) return hasLineupContent.value

  return hasChanges.value
})

async function confirmSave(): Promise<void> {
  await props.confirmUpdateLineup(
    lineup.value,
    initialLineup.value,
    updateLineups,
    captain.value,
    isPendingDeferredClear.value,
  )
}

function handleProbableLineupClearedLocally(): void {
  const emptyLineup = toSortedLineupModel(createLineupModel([]))
  lineup.value = emptyLineup
  initialLineup.value = structuredClone(emptyLineup)
}

async function loadLineupForModal(): Promise<void> {
  if (isParticipantPendingDeferredClear(props.participant.id)) {
    handleProbableLineupClearedLocally()
    return
  }

  await fetchLineupWhenNotPendingDeferredClear()
}

async function fetchLineupWhenNotPendingDeferredClear(): Promise<void> {
  if (isParticipantPendingDeferredClear(props.participant.id)) {
    handleProbableLineupClearedLocally()
    return
  }

  await fetchLineup()
}

await runLoader(Promise.all([fetchSeasonSquad(), loadLineupForModal()]))
</script>

<template>
  <Modal
    :confirmButton="{
      text: 'Save',
      confirm: confirmSave,
      disabled: !canSave,
    }"
    :withCloseShortcuts="false"
    @close="emit('close')"
  >
    <template #title>
      <LineupsTitle
        :participant
        @close="emit('close')"
      />
    </template>
    <div class="relative @xs:w-[77vw] @lg:w-[49.125rem]">
      <Lineups
        class="w-full"
        :modelValue="lineup"
        :config
        :squad="seasonSquadSubParticipants"
        @update:modelValue="updateLineupModel"
      />
      <ClearProbableLineupOverlay
        :participant
        @clear="handleProbableLineupClearedLocally"
      />
      <slot
        name="overlay"
        :fetchLineup="fetchLineupWhenNotPendingDeferredClear"
      />
    </div>
  </Modal>
</template>
